import React, { useState } from 'react';

import {
  Button,
  Drawer,
  Row,
  Col,
  Typography,
  Space,
  Modal,
  Alert,
} from 'antd';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';

import { usePatchInspection } from '../api/patchInspectionCommand';
import { InspectionClosure } from '../components/InspectionClosure';
import { InspectionSession } from '../components/InspectionSession';
import { Search } from '../components/Search';
import { useInspectionTicketView } from '../hooks/useInspectionTicketView';
import { useInspectionStore } from '../store/inspection.store';
import CheckIconV2 from '@/components/icons/CheckIconV2';
import CrossIcon from '@/components/icons/CrossIcon';
import { scopes } from '@/config';
import useAppState from '@/hooks/useAppState';
import { useAuth0 } from '@/shared/auth0';

const Title = Typography.Title;
const Text = Typography.Text;
const Paragraph = Typography.Paragraph;

export const SecurityCheck = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth0();

  const userScopes = useAppState((state) => state.scopes);

  const { mutateAsync: closeInspection } = usePatchInspection({});
  const ticket = useInspectionStore((state) => state.ticket);
  const note = useInspectionStore((state) => state.note);
  const { clearInspectionTicket } = useInspectionTicketView();

  const [isVisible, setIsVisible] = useState(false);
  const [securityInspectionStep, setSecurityInspectionStep] = useState(0);
  const [selectedShipment, setSelectedShipment] = useState<any>(null);
  const [isWarningModalVisible, setIsWarningModalVisible] = useState(false);

  // ask Scope to Backend
  const isAuthorize =
    userScopes.includes(scopes.MANAGE_SHIPMENTS) &&
    userScopes.includes(scopes.MANAGE_PRODUCT);

  const cancelInspection = async () => {
    try {
      await closeInspection({
        ticketId: ticket ?? '',
        close: true,
      });
      setIsWarningModalVisible(false);
      setSecurityInspectionStep(0);
      setSelectedShipment(null);
    } catch (error) {
      console.error('Error canceling inspection:', error);
    }
  };

  return (
    <>
      {!isVisible ? (
        <>
          <Row
            gutter={32}
            justify="space-between"
            style={{
              padding: '1.5rem',
              paddingTop: '1.125rem',
            }}
          >
            <Col>
              <Title
                style={{
                  fontSize: '1.75rem',
                  fontWeight: 500,
                  lineHeight: '130%',
                  marginBottom: '1.5rem',
                }}
              >
                MOBILE BACKOFFICE
              </Title>
              {!isAuthorize ? (
                <Alert
                  type="error"
                  showIcon
                  message="You do not have the necessary permissions to access the Support Mobile features."
                />
              ) : null}
              <Title level={4}> Welcome </Title>
              <Paragraph
                style={{
                  fontSize: '1rem',
                  fontWeight: 400,
                  lineHeight: '135%',
                }}
              >
                {`You are logged in as ${user.email}. Depending on your
              authorizations, you will be able to access the features below.`}
              </Paragraph>
            </Col>
          </Row>
          <Space
            direction="vertical"
            size="middle"
            style={{
              position: 'fixed',
              bottom: '0',

              paddingLeft: '1.5rem',
              paddingRight: '1.5rem',
              paddingBottom: '1.5rem',
              width: '100%',
            }}
          >
            <Button
              onClick={() => {
                setIsVisible(true);
              }}
              style={{
                width: '100%',
                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.5rem',
              }}
              type="primary"
              disabled={!isAuthorize}
            >
              Security inspection
            </Button>

            <Button
              style={{
                width: '100%',

                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.5rem',
              }}
              onClick={() => navigate('/product')}
            >
              Access to AdminPanel
            </Button>
            <Button
              style={{
                width: '100%',

                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.5rem',
              }}
              type="link"
              onClick={() => {
                logout({
                  returnTo: window.location.origin,
                });
              }}
            >
              Logout
            </Button>
          </Space>
        </>
      ) : null}
      <Drawer
        width="100%"
        open={isVisible}
        closeIcon={false}
        footer={
          securityInspectionStep === 1 ? (
            <Button
              type="primary"
              style={{
                width: '100%',
                display: 'flex',
                gap: '8px',
                alignContent: 'center',
                justifyContent: 'center',
              }}
              onClick={async () => {
                setSecurityInspectionStep(2);
              }}
            >
              <CheckIconV2 /> <span>End Inspection</span>
            </Button>
          ) : securityInspectionStep === 2 ? (
            <Button
              type="primary"
              style={{
                width: '100%',
                display: 'flex',
                gap: '8px',
                alignContent: 'center',
                justifyContent: 'center',
              }}
              onClick={async () => {
                await closeInspection({
                  ticketId: ticket ?? '',
                  note: note,
                });

                clearInspectionTicket(); // Reset the ticket in the store
                setSecurityInspectionStep(0);
              }}
            >
              <span>Save and exit</span>
            </Button>
          ) : null
        }
        styles={{
          body: {
            padding: '0px',
          },
        }}
      >
        <Row>
          <Col
            span={21}
            style={{
              paddingTop: '1.125rem',
            }}
          >
            <Text
              style={{
                fontSize: '1.75rem',
                fontWeight: 500,
                lineHeight: '130%',
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem',
              }}
            >
              {securityInspectionStep === 2
                ? 'Inspection summary'
                : 'Security inspection'}
            </Text>
          </Col>
          <Col span={3} style={{ paddingTop: '1.125rem' }}>
            <Button
              type="text"
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '.125rem',
              }}
              onClick={() => {
                if (securityInspectionStep > 0) {
                  setIsWarningModalVisible(true);
                } else {
                  setIsVisible(false);
                  setSecurityInspectionStep(0);
                }
              }}
              //  ;
            >
              <CrossIcon width="40px" height="40px" stroke="#000" />
            </Button>
          </Col>
        </Row>
        {securityInspectionStep === 0 && isMobile ? (
          <Search
            updateStep={setSecurityInspectionStep}
            setSelectedShipment={setSelectedShipment}
          />
        ) : null}

        {securityInspectionStep === 1 ? (
          <InspectionSession
            shipment={selectedShipment}
            onEndInspection={() => {}}
            isInspectionStarted={true}
          />
        ) : null}

        {securityInspectionStep === 2 ? (
          <InspectionClosure
            updateStep={() => setSecurityInspectionStep(0)}
            inspectionTicketId={ticket ?? ''}
          />
        ) : null}
        <Modal
          title="Warning"
          open={isWarningModalVisible}
          footer={null}
          closeIcon={false}
        >
          <>
            <Text>
              Please note! This inspection is in progress. Do you really want to
              leave this page?
            </Text>

            <Space.Compact
              direction="vertical"
              size="middle"
              style={{
                width: '100%',
                paddingTop: '1rem',
              }}
            >
              <Button
                type="primary"
                style={{
                  borderRadius: '0.125rem',
                  marginBottom: '0.5rem',
                }}
                onClick={() => {
                  setIsWarningModalVisible(false);
                }}
              >
                Stay on the page
              </Button>
              <Button
                onClick={() => {
                  cancelInspection();
                }}
                style={{
                  borderRadius: '.125rem',
                }}
              >
                Leave the page anyway
              </Button>
            </Space.Compact>
          </>
        </Modal>
      </Drawer>
    </>
  );
};
